/**
 * RestoID — social account linking ("we found an account that matches").
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream linking logic. The page's three
 * guards (`socialAccountNotExistErrorDataGuard` over the nav state, the route's
 * connectorId, the Social verification id) and the container's two actions
 * (`useBindSocialRelatedUser`, `useSocialRegister`) are copied verbatim; only the
 * markup is ours. Replaces Logto's `SecondaryPageLayout` (NavBar + a second copy
 * of the title) plus the `SocialLinkAccount` container. Swapped in via
 * `src/App.tsx`. Structure follows `custom/design-reference/app.js`
 * SocialLinkScreen: head -> related-account card -> bind CTA -> ghost "create a
 * new one instead".
 *
 * Deliberate differences from upstream, and why:
 *  - The related account moves out of the CTA label and into the design's card
 *    (connector disc + identifier + provider name). The identifier stays MASKED
 *    via the upstream `maskEmail`/`maskPhone`: this screen is reachable by
 *    whoever controls the social account, so the existing account's full address
 *    must not be shown. Losing that mask would be a data leak, not a restyle.
 *  - The CTA therefore reads `action.bind_and_continue` instead of `action.bind`
 *    ("Link with {{address}}") — the address is already in the card, and this is
 *    what the design reference specifies. Same handler, same loading state.
 *  - The connector's disc and name come from the connector METADATA
 *    (`useConnectors().findConnectorById`, exactly as the upstream SocialLanding
 *    container does), so no vendor color is restated here and any connector
 *    renders correctly — the Yandex red disc is simply the logo Logto already
 *    serves. Only the light/dark VARIANT is picked differently: see
 *    `getConnectorLogo` in `custom/components/connector-meta.ts`.
 *  - Upstream's `description.skip_social_linking` hint + `TextLink` become a muted
 *    line + `GhostAction`. Both phrase keys, the `getCreateAccountActionText`
 *    switch over `signUpMethods`, and the `signInMode !== SignInMode.SignIn` guard
 *    that hides the whole block are unchanged.
 *  - The back action moves from SecondaryPageLayout's NavBar onto `ScreenHead`
 *    (same `navigate(-1)`), and `PageMeta` keeps the same dynamic title key.
 */
import { SignInIdentifier, SignInMode, VerificationType } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { is } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useBindSocialRelatedUser from '@/containers/SocialLinkAccount/use-social-link-related-user';
import useConnectors from '@/hooks/use-connectors';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useSocialRegister from '@/hooks/use-social-register';
import DynamicT from '@/shared/components/DynamicT';
import { socialAccountNotExistErrorDataGuard, type SocialRelatedUserInfo } from '@/types/guard';
import { maskEmail, maskPhone } from '@/utils/format';

import { getConnectorLogo, getConnectorName } from '../components/connector-meta';
import { Button, C, FONT, GhostAction, Icon, ScreenHead, Stack } from '../components/restoid';
import useRidTheme from '../components/use-rid-theme';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

type RouteParameters = {
  connectorId: string;
};

/** Verbatim from the upstream page — picks the title by what sign-up allows linking. */
const getPageTitle = (signUpMethods: SignInIdentifier[]): TFuncKey => {
  if (
    signUpMethods.includes(SignInIdentifier.Email) &&
    signUpMethods.includes(SignInIdentifier.Phone)
  ) {
    return 'description.link_email_or_phone';
  }

  if (signUpMethods.includes(SignInIdentifier.Email)) {
    return 'description.link_email';
  }

  if (signUpMethods.includes(SignInIdentifier.Phone)) {
    return 'description.link_phone';
  }

  return 'description.bind_account_title';
};

/** Verbatim from the upstream container — the "don't link, make a new one" label. */
const getCreateAccountActionText = (signUpMethods: SignInIdentifier[]): TFuncKey => {
  if (
    signUpMethods.includes(SignInIdentifier.Email) &&
    signUpMethods.includes(SignInIdentifier.Phone)
  ) {
    return 'action.link_another_email_or_phone';
  }

  if (signUpMethods.includes(SignInIdentifier.Email)) {
    return 'action.link_another_email';
  }

  if (signUpMethods.includes(SignInIdentifier.Phone)) {
    return 'action.link_another_phone';
  }

  return 'action.create_account_without_linking';
};

/** The connector's 40px disc: its real logo, or a neutral glyph if we have no metadata. */
const ConnectorDisc = ({ logo }: { logo?: string }) => (
  <span
    style={{
      flex: '0 0 auto',
      display: 'grid',
      placeItems: 'center',
      width: 40,
      height: 40,
      borderRadius: '50%',
      overflow: 'hidden',
      background: logo ? C.field : C.tile,
      border: `1px solid ${logo ? C.line : C.tileLine}`,
      color: C.brand,
    }}
  >
    {logo ? (
      // Decorative: the provider's name is spelled out right beside it.
      // `contain`, not `cover`: a square mark (Yandex's red disc, and every other
      // connector logo Logto ships) fills the 40px circle identically either way,
      // but a wide wordmark would be cropped by `cover` and is merely letterboxed
      // by `contain`.
      <img src={logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    ) : (
      <Icon name="user" size={20} />
    )}
  </span>
);

type ActionsProps = {
  connectorId: string;
  verificationId: string;
  relatedUser: SocialRelatedUserInfo;
};

/**
 * The upstream `SocialLinkAccount` container, restated as RestoID markup. Hooks,
 * their order and both handlers are unchanged.
 */
const RelatedAccountActions = ({ connectorId, verificationId, relatedUser }: ActionsProps) => {
  const {
    i18n: { language },
  } = useTranslation();
  const { signUpMethods, signInMode } = useSieMethods();
  const { findConnectorById } = useConnectors();
  const isDark = useRidTheme() === 'dark';

  const bindSocialRelatedUser = useBindSocialRelatedUser();
  const [isBindingUser, setIsBindingUser] = useState(false);

  const registerWithSocial = useSocialRegister(connectorId);

  const actionText = getCreateAccountActionText(signUpMethods);

  const { type, value } = relatedUser;
  const maskedAddress = type === 'email' ? maskEmail(value) : maskPhone(value);

  const result = findConnectorById(connectorId);
  // Upstream's SocialLanding container falls back to the bare id when the connector
  // is not in the sign-in experience settings; do the same rather than render blank.
  const providerName = result ? getConnectorName(result, language) : connectorId;
  const logo = result ? getConnectorLogo(result, isDark) : undefined;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 16,
          borderRadius: 12,
          background: C.card,
          border: `1px solid ${C.lineSoft}`,
        }}
      >
        <ConnectorDisc logo={logo} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              color: C.text,
              font: `600 15px/1.35 ${FONT}`,
              overflowWrap: 'anywhere',
            }}
          >
            {maskedAddress}
          </span>
          <span
            style={{
              display: 'block',
              marginTop: 2,
              color: C.sub,
              font: `400 13px/1.4 ${FONT}`,
              overflowWrap: 'anywhere',
            }}
          >
            {providerName}
          </span>
        </span>
      </div>

      <Button
        title={<DynamicT forKey="action.bind_and_continue" />}
        isLoading={isBindingUser}
        onClick={async () => {
          setIsBindingUser(true);
          await bindSocialRelatedUser(verificationId);
          setIsBindingUser(false);
        }}
      />

      {signInMode !== SignInMode.SignIn && (
        <div>
          <p
            style={{
              margin: '0 0 10px',
              textAlign: 'center',
              color: C.sub,
              font: `400 13px/1.5 ${FONT}`,
            }}
          >
            <DynamicT forKey="description.skip_social_linking" />
          </p>
          <GhostAction
            onClick={() => {
              void registerWithSocial(verificationId);
            }}
          >
            <DynamicT forKey={actionText} />
          </GhostAction>
        </div>
      )}
    </>
  );
};

const SocialLinkAccount = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { connectorId } = useParams<RouteParameters>();
  const { state } = useLocation();
  const { signUpMethods } = useSieMethods();
  const { verificationIdsMap } = useContext(UserInteractionContext);
  const verificationId = verificationIdsMap[VerificationType.Social];

  // The three upstream guards, in the upstream order and with the upstream messages.
  if (!is(state, socialAccountNotExistErrorDataGuard)) {
    return <ErrorPage rawMessage="Missing relate account info" />;
  }

  if (!connectorId) {
    return <ErrorPage rawMessage="Connector not found" />;
  }

  if (!verificationId) {
    return <ErrorPage title="error.invalid_session" rawMessage="Verification ID not found" />;
  }

  const { relatedUser } = state;
  const title = getPageTitle(signUpMethods);

  return (
    <FirstScreenLayout pageMeta={{ titleKey: title }}>
      <Stack gap={20}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={<DynamicT forKey={title} />}
          sub={t('description.social_bind_with_existing')}
        />
        <RelatedAccountActions
          connectorId={connectorId}
          verificationId={verificationId}
          relatedUser={relatedUser}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

export default SocialLinkAccount;
